import React from 'react';
import styled from 'styled-components';
import { Image } from '../../../kit/Image';
import { Layout } from "../../../kit/Layout";
import { Paper } from '../../../kit/Paper';
import { Text } from '../../../kit/Text';
import { DateUtils } from '../../../core/utils/dateUtils';
import { RoundScheduleItem } from '../../../syncstream/utils/RoundUtils';
import {
    HSAdministeredDrug,
    HSDrug,
    WardDto,
    HSFacilityGroup,
    HSPackedMedication
} from "server-openapi";
import { DrugLabel } from '../../ResidentDetails/components/medicationInformation/DrugDetails';
import { useHistory } from 'react-router';
import { useApiUtils } from '../../../syncstream/utils/hooks/useApiUtils';
import { useStore } from '../../../core/storage/hooks/UseStore';
import itiriri from 'itiriri';
import { useSyncCenter } from '../../../syncstream/SyncCenterProvider';
import { CommentsCompact } from "../../../pages/ResidentDetails/components/drugDetails/CommentsCompact";
import { OutcomesCompact } from "../../../pages/ResidentDetails/components/drugDetails/OutcomesCompact";
import { DrugUtils } from "../../../syncstream/utils/DrugUtils";
import { StatusIconContainer } from "../../../pages/ResidentDetails/components/drugDetails/DrugAdministrationHistory";
import { StatusIcon } from "../../../pages/ResidentDetails/components/drugDetails/StatusIcon";
import { Grid } from "../../../kit/Grid";
import { format } from "date-fns";

enum MedicationStatus {
    NOT_ADMINISTERED = 'NOT ADMINISTERED',
    MISSED = 'MISSED'
}

interface DrugDetailsProps {
    drug: HSDrug | undefined;
    facility: WardDto;
    scheduleItem: RoundScheduleItem;
    administeredDrug?: HSAdministeredDrug;
}

interface TimeCriticalDosesCardProps {
    patientId?: number;
    scheduleItem: RoundScheduleItem;
    facilityGroup: HSFacilityGroup;
    drugStore: ReadonlyMap<string, HSDrug>;
    facilityStore: ReadonlyMap<string, WardDto>;
}

const getDoseTime = (scheduleItem: RoundScheduleItem): string =>
    DateUtils.dateTo24HourTimeString(DateUtils.toDate(scheduleItem.packedMedication.doseTimestamp ?? ''));

const getMedicationStatus = (packedMedication?: HSPackedMedication, dosedDrug?: HSAdministeredDrug): string => {
    if (dosedDrug) {
        const apiUtils = useApiUtils();
        return apiUtils.residentDetails.reasonCodeToString(dosedDrug.reasonCode).toUpperCase();
    }

    const isFutureDose = packedMedication?.doseTimestamp &&
        Date.parse(packedMedication.doseTimestamp) > Date.now();

    return isFutureDose ? MedicationStatus.NOT_ADMINISTERED : MedicationStatus.MISSED;
};

const TimeCriticalDoseDetail: React.FC<DrugDetailsProps> = ({ drug, facility, scheduleItem, administeredDrug }) => {
    const userApiUtils = useApiUtils().users;
    const medicationStatus = getMedicationStatus(scheduleItem.packedMedication, administeredDrug);

    return (
        <Container gap={0}>
            <InformationBoxDoseDetail padding={'2rem'} horizontal gap={2}>
                {administeredDrug?.reasonCode || medicationStatus === MedicationStatus.MISSED
                    ?(
                        <StatusIconContainer reasonCode={administeredDrug?.reasonCode}>
                            <StatusIcon reasonCode={administeredDrug?.reasonCode}/>
                        </StatusIconContainer>
                    )
                    :(
                        <NoStatusSpace/>
                    )
                }
                <Layout gap={1} style={{width: '100%'}}>
                    <DrugDetailContainer alignItemsCenter cols={3} colsTemplate={'3fr 1fr 1.5fr'}>
                        <Layout>
                            {drug && <DrugLabel drug={drug} isCeased={false} isPsychotropicConsent={false} />}
                            <Text>
                                {administeredDrug?.administeredAt && administeredDrug.administeredAt.length > 0
                                    ? `${format(DateUtils.toDate(administeredDrug.administeredAt), 'd MMM y')} at ${format(
                                        DateUtils.toDate(administeredDrug.administeredAt),
                                        'HH:mm',
                                    )} by ${userApiUtils.getUserFullNameAndRoleFromSubjectId(administeredDrug.administeredBySubjectId)}`
                                    : ``}
                            </Text>
                        </Layout>
                        <DosageSquareBox>{administeredDrug?.administeredDosage ?? scheduleItem.packedMedication.dosage}</DosageSquareBox>
                        <ActionsLayout horizontal gap={0.5}>
                            <Text style={{textTransform: 'uppercase', justifySelf: 'center'}}>
                                {getMedicationStatus(scheduleItem.packedMedication, administeredDrug)}
                            </Text>
                        </ActionsLayout>
                    </DrugDetailContainer>
                    <Layout gap={0.5}>
                        {administeredDrug?.administeredDrugComments?.map((comment, index) => {
                            return <CommentsCompact facilityGroupId={facility.facilityGroupId ?? 0} comment={DrugUtils.extractCompactCommentInfo(comment)} key={index}/>;
                        })}
                        {administeredDrug?.administeredDrugOutcomes?.map((outcome, index) => {
                            return <OutcomesCompact outcome={DrugUtils.extractCompactCommentInfo(outcome)} key={index}/>;
                        })}
                    </Layout>
                </Layout>
            </InformationBoxDoseDetail>
        </Container>
    );
};

export const TimeCriticalDosesCard: React.FC<TimeCriticalDosesCardProps> = ({
    scheduleItem,
    facilityGroup,
    drugStore,
    facilityStore
}) => {
    const history = useHistory();
    const services = useSyncCenter();
    const apiUtils = useApiUtils();
    const roundStore = useStore(services.rounds.store).store;
    const patient = scheduleItem.patient;
    const facility = facilityStore.get(patient.facility?.toString() ?? '');
    const doseTime = getDoseTime(scheduleItem);
    const navigate = () => history.push(`/resident-details/${patient.hsId}/`);
    const rounds = itiriri(roundStore.values()).toArray();

    return (
        <Paper>
            <InformationBox horizontal gap={1}>
                <DoseTimestampColumn2>
                    <Text weight={'bold'}>{doseTime}</Text>
                </DoseTimestampColumn2>
                <PatientImageColumn style={{ cursor: 'pointer' }} onClick={navigate}>
                    {patient.imageUrl && (
                        <PatientImage
                            src={patient.imageUrl}
                            facilityGroupId={facilityGroup.hsId ?? 0}
                        />
                    )}
                </PatientImageColumn>
                <PatientDetailColumn>
                    <Text weight={'bold'}>{apiUtils.patients.getDisplayPatientName(patient)}</Text>
                    <Text>{facility?.name} Rm {patient.roomNumber}</Text>
                </PatientDetailColumn>
                <TimeCriticalDoseDetail
                    drug={drugStore.get(scheduleItem.packedMedication.drugHsId?.toString() ?? '')}
                    scheduleItem={scheduleItem}
                    facility={facility!}
                    administeredDrug={apiUtils.rounds.getSortedHistoricalAdministrationsForPatientAndPackedMedication(
                        patient.hsId ?? 0,
                        scheduleItem.packedMedication,
                        rounds
                    )?.[0]?.administeredDrug}
                />
            </InformationBox>
        </Paper>
    );
};

const InformationBox = styled(Layout)`
    display: flex;
    align-items: center;
`;

const InformationBoxDoseDetail = styled(Layout)`
    background: ${(p) => p.theme.backgrounds.lighter.bg};
    color: black;
    width: 100%;
    min-height: 100px;
    display: flex;
    align-items: center;
`;

const Container = styled(Layout)`
  align-items: flex-start;
  flex-direction: column;
  display: flex;
  width: 100%;
`;

const PatientImageColumn = styled.div`
  padding-right: 1.25rem;
`;

const PatientImage = styled(Image)`
  height: 60px;
  width: 60px;
  object-fit: cover;
  border-radius: 999999999px;
`;

const PatientDetailColumn = styled.div`
  padding-right: 1.25rem;
  flex-basis: 35%;
`;

const DrugDetailContainer = styled(Grid)`
    width: 100%;
`;

const DosageSquareBox = styled.div`
    justify-self: flex-end;
    line-height: 1.25;
    text-align: center;
    padding: 0.625rem;
    border-color: black;
    border-width: 3px;
    width: 3rem;
    margin-right: 0.5rem;
    border-style: solid;
`;

const ActionsLayout = styled(Layout)`
    display: flex;
    align-items: center;
    justify-self: center;
`;

const DoseTimestampColumn2 = styled(Layout)`
  padding-right: 1.25rem;
  flex-basis: 5rem;
`;

const NoStatusSpace = styled.div`
    width: 30px;
    height: 30px;
    display: flex;
`;